import { useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { createAdminTicketLink, listTickets, voidTicket, ticketStatusCounts } from "@/lib/api";
import { statusLabel, fmtDateTime } from "@/lib/formatters";
import { useIsStaff } from "@/lib/use-staff";
import { useTicketsRealtime } from "@/lib/use-tickets-realtime";

interface RawTicket {
  id: string;
  ticket_no: string;
  status: string;
  checked_in_at: string | null;
  order_id: string;
  ticket_type: { name?: string; code?: string } | null;
  attendee: { first_name?: string; last_name?: string; email?: string } | null;
  order: {
    order_no?: string;
    buyer_email?: string;
    flow?: string;
    ambassador?: { name?: string; email?: string } | { name?: string; email?: string }[] | null;
  } | null;
}

// Verrijkt ticket voor lijst, CSV en deurlijst: aantal in bestelling + ambassadeur-herkenning.
interface DoorTicket extends RawTicket {
  orderCount: number;
  ambName: string | null;   // naam van de ambassadeur bij deze bestelling (indien van toepassing)
  isAmbSelf: boolean;       // deze houder IS de ambassadeur (krijgt het speldje)
}

function firstOf<T>(v: T | T[] | null | undefined): T | null {
  if (Array.isArray(v)) return v[0] ?? null;
  return v ?? null;
}

function fullName(a: RawTicket["attendee"]): string {
  const n = `${a?.first_name ?? ""} ${a?.last_name ?? ""}`.trim();
  return n || "—";
}

function enrich(tickets: RawTicket[]): DoorTicket[] {
  const counts = new Map<string, number>();
  for (const t of tickets) counts.set(t.order_id, (counts.get(t.order_id) ?? 0) + 1);

  return tickets.map((t) => {
    const orderCount = counts.get(t.order_id) ?? 1;
    const amb = firstOf(t.order?.ambassador ?? null);
    const ambName = amb?.name ? String(amb.name) : null;
    const attEmail = (t.attendee?.email ?? "").trim().toLowerCase();
    const ambEmail = (amb?.email ?? "").trim().toLowerCase();
    // De ambassadeur zelf = e-mailmatch óf de bestelling heeft maar 1 ticket.
    const isAmbSelf = !!ambName && ((attEmail.length > 0 && attEmail === ambEmail) || orderCount === 1);
    return { ...t, orderCount, ambName, isAmbSelf };
  });
}

function sortByName(rows: DoorTicket[]): DoorTicket[] {
  return [...rows].sort((a, b) => {
    const an = `${a.attendee?.last_name ?? ""} ${a.attendee?.first_name ?? ""}`.trim().toLowerCase();
    const bn = `${b.attendee?.last_name ?? ""} ${b.attendee?.first_name ?? ""}`.trim().toLowerCase();
    if (!an && bn) return 1;
    if (an && !bn) return -1;
    return an.localeCompare(bn, "nl");
  });
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsv(rows: DoorTicket[], filename: string) {
  const header = [
    "ticket_no", "voornaam", "achternaam", "email", "type",
    "bestelnr", "aantal_in_bestelling", "ambassadeur", "status", "gescand_op",
  ];
  const lines = [header.join(",")];
  for (const r of sortByName(rows)) {
    const a = r.attendee ?? {};
    lines.push([
      csvEscape(r.ticket_no),
      csvEscape(a.first_name),
      csvEscape(a.last_name),
      csvEscape(a.email),
      csvEscape(r.ticket_type?.name ?? ""),
      csvEscape(r.order?.order_no ?? ""),
      csvEscape(r.orderCount),
      csvEscape(r.isAmbSelf ? (r.ambName ?? "ambassadeur") : r.ambName ? `groep: ${r.ambName}` : ""),
      csvEscape(r.status),
      csvEscape(r.checked_in_at ?? ""),
    ].join(","));
  }
  // BOM zodat Excel UTF-8 herkent
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function htmlEscape(s: unknown): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));
}

// Print-vriendelijke deurlijst: alfabetisch op naam, met afvink-vakje. Backup als scannen faalt.
function printDoorList(rows: DoorTicket[], scopeLabel: string) {
  const sorted = sortByName(rows);
  const total = sorted.length;
  const ambCount = sorted.filter((r) => r.isAmbSelf).length;
  const scanned = sorted.filter((r) => r.status === "scanned").length;
  const voidCount = sorted.filter((r) => r.status === "void").length;
  const generated = dayjs().format("DD-MM-YYYY HH:mm");

  const body = sorted.map((r) => {
    const naam = htmlEscape(fullName(r.attendee));
    const amb = r.isAmbSelf
      ? `<span class="amb">🎖️ ${htmlEscape(r.ambName ?? "ambassadeur")}</span>`
      : r.ambName
        ? `<span class="grp">groep: ${htmlEscape(r.ambName)}</span>`
        : "";
    const voidCls = r.status === "void" ? " class=\"void\"" : "";
    const statusTxt = r.status === "void" ? "GEBLOKKEERD" : r.status === "scanned" ? "al gescand" : "";
    return `<tr${voidCls}>
      <td class="chk"></td>
      <td class="naam">${naam}</td>
      <td class="mono">${htmlEscape(r.ticket_no)}</td>
      <td>${htmlEscape(r.ticket_type?.name ?? "")}</td>
      <td class="num">${r.orderCount > 1 ? r.orderCount : ""}</td>
      <td>${amb}</td>
      <td class="st">${statusTxt}</td>
    </tr>`;
  }).join("");

  const doc = `<!DOCTYPE html>
<html lang="nl"><head><meta charset="utf-8"><title>Deurlijst — Ode aan de Haring 2026</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #111; margin: 0; padding: 16px 18px; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .meta { font-size: 11px; color: #555; margin-bottom: 4px; }
  .totals { font-size: 12px; margin: 6px 0 12px; }
  .totals b { font-size: 14px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #bbb; padding: 4px 6px; text-align: left; font-size: 11px; vertical-align: top; }
  th { background: #eee; font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
  td.chk { width: 22px; }
  td.chk::before { content: ""; display: inline-block; width: 13px; height: 13px; border: 1.5px solid #333; border-radius: 2px; }
  td.naam { font-weight: 600; }
  td.mono { font-family: ui-monospace, Menlo, Consolas, monospace; white-space: nowrap; }
  td.num { text-align: center; width: 44px; }
  td.st { color: #b00; font-size: 10px; }
  .amb { color: #9a6a00; font-weight: 700; white-space: nowrap; }
  .grp { color: #777; font-size: 10px; white-space: nowrap; }
  tr.void td { color: #999; text-decoration: line-through; }
  tr.void td.chk::before { border-color: #bbb; }
  .toolbar { margin-bottom: 12px; }
  .toolbar button { font-size: 13px; padding: 6px 14px; cursor: pointer; }
  @media print { .toolbar { display: none; } @page { size: A4; margin: 12mm; } th { background: #eee !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head>
<body>
  <div class="toolbar"><button onclick="window.print()">🖨️ Printen</button></div>
  <h1>Deurlijst — Ode aan de Haring 2026</h1>
  <div class="meta">Gegenereerd ${generated} · selectie: ${htmlEscape(scopeLabel)} · sorteer: naam (A→Z)</div>
  <div class="totals"><b>${total}</b> tickets · <b>${ambCount}</b> ambassadeur(s) met speldje · ${scanned} al gescand${voidCount ? ` · ${voidCount} geblokkeerd` : ""}</div>
  <table>
    <thead><tr>
      <th>✓</th><th>Naam</th><th>Ticketnr</th><th>Type</th><th>Aantal</th><th>Ambassadeur</th><th>Status</th>
    </tr></thead>
    <tbody>${body}</tbody>
  </table>
</body></html>`;

  const w = window.open("", "_blank");
  if (!w) {
    alert("Pop-up geblokkeerd. Sta pop-ups toe voor deze site om de deurlijst te printen.");
    return;
  }
  w.document.open();
  w.document.write(doc);
  w.document.close();
}

export function TicketsIndex() {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [ticketLinkBusy, setTicketLinkBusy] = useState<string | null>(null);
  const staff = useIsStaff();
  const qc = useQueryClient();

  const { data: rawTickets, isLoading } = useQuery({
    queryKey: ["tickets", status, q],
    queryFn: () => listTickets({ status: status || undefined, q: q || undefined }),
  });

  const tickets = useMemo(() => enrich((rawTickets ?? []) as unknown as RawTicket[]), [rawTickets]);

  // Ingewisseld-tab: laatst binnengekomen bovenaan (handig live aan de deur).
  const displayed = useMemo(() => {
    if (status !== "scanned") return tickets;
    return [...tickets].sort((a, b) => (b.checked_in_at ?? "").localeCompare(a.checked_in_at ?? ""));
  }, [tickets, status]);

  // Live tellers — realtime via check-ins van alle apparaten, met 20s-poll als vangnet.
  const { data: counts } = useQuery({
    queryKey: ["ticket-counts"],
    queryFn: ticketStatusCounts,
    refetchInterval: 20000,
  });

  // Realtime: zodra een ander apparaat incheckt, ververs lijst + teller (gebundeld per 0,8s).
  const rtTimer = useRef<number | null>(null);
  useTicketsRealtime(() => {
    if (rtTimer.current) return;
    rtTimer.current = window.setTimeout(() => {
      rtTimer.current = null;
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["ticket-counts"] });
    }, 800);
  });

  const voidMut = useMutation({
    mutationFn: (id: string) => voidTicket(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["ticket-counts"] });
    },
  });

  const scopeLabel = status ? statusLabel(status) : "alle";

  function exportCurrent() {
    if (tickets.length === 0) return;
    downloadCsv(tickets, `haringparty-tickets-${scopeLabel}-${dayjs().format("YYYYMMDD-HHmm")}.csv`);
  }

  function printList() {
    if (tickets.length === 0) return;
    printDoorList(tickets, scopeLabel);
  }

  async function openTicketAccess(ticketId: string, mode: "page" | "pdf") {
    const busyKey = `${mode}:${ticketId}`;
    setTicketLinkBusy(busyKey);
    try {
      const link = await createAdminTicketLink({ ticketId });
      window.open(mode === "page" ? link.ticket_url : link.pdf_url, "_blank", "noopener");
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    } finally {
      setTicketLinkBusy(null);
    }
  }

  const validTotal = counts ? counts.total - counts.voided : 0;
  const pctInside = validTotal ? Math.round((counts!.scanned / validTotal) * 100) : 0;

  return (
    <>
      {counts && (
        <div className="kpis" style={{ marginBottom: "1rem" }}>
          <div className="kpi">
            <div className="l">Binnen (ingecheckt)</div>
            <div className="v" style={{ color: "#6ee7b7" }}>{counts.scanned}</div>
            <div className="delta">{pctInside}% van {validTotal} · ververst automatisch</div>
          </div>
          <div className="kpi">
            <div className="l">Nog niet binnen</div>
            <div className="v">{counts.issued}</div>
            <div className="delta">Geldige tickets nog buiten</div>
          </div>
          <div className="kpi">
            <div className="l">Totaal geldig</div>
            <div className="v">{validTotal}</div>
            <div className="delta">{counts.voided > 0 ? `${counts.voided} geblokkeerd` : "verkochte tickets"}</div>
          </div>
        </div>
      )}

      <div className="topbar-actions" style={{ marginBottom: "1rem" }}>
        <input
          className="search"
          placeholder="🔍 Zoek QR, naam, email..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button
          type="button"
          className="btn-admin sm"
          onClick={printList}
          disabled={tickets.length === 0}
          title="Print een alfabetische deurlijst met afvink-vakjes — backup als scannen niet werkt"
        >
          🖨️ Deurlijst ({tickets.length})
        </button>
        <button
          type="button"
          className="btn-admin ghost sm"
          onClick={exportCurrent}
          disabled={tickets.length === 0}
          title="Download deze lijst als CSV (Excel)"
        >
          📥 CSV
        </button>
      </div>

      <div className="tabs">
        {[
          { v: "", l: "Alle" },
          { v: "issued", l: "Actief" },
          { v: "scanned", l: "Ingewisseld" },
          { v: "void", l: "Geblokkeerd" },
        ].map((t) => (
          <button key={t.v} className={`tab ${status === t.v ? "active" : ""}`} onClick={() => setStatus(t.v)}>{t.l}</button>
        ))}
      </div>

      <div className="panel">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>QR-code</th><th>Houder</th><th>Bestelnr</th><th>Type</th><th>Status</th><th>Gescand</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="empty"><span className="spinner" /> Laden…</td></tr>}
              {displayed.map((t) => (
                <tr key={t.id}>
                  <td><code style={{ fontFamily: "var(--f-mono)", fontSize: "0.78rem", color: "var(--c-orange)" }}>{t.ticket_no}</code></td>
                  <td>
                    {fullName(t.attendee)}
                    {t.isAmbSelf && <span title={`Ambassadeur: ${t.ambName}`} style={{ marginLeft: 6 }}>🎖️</span>}
                    {!t.isAmbSelf && t.ambName && <span title={`Groep van ambassadeur ${t.ambName}`} style={{ marginLeft: 6, opacity: 0.6, fontSize: "0.72rem" }}>· groep {t.ambName}</span>}
                    <br /><small>{t.attendee?.email}</small>
                  </td>
                  <td>{t.order?.order_no}{t.orderCount > 1 && <><br /><small style={{ opacity: 0.6 }}>{t.orderCount}× in bestelling</small></>}</td>
                  <td>{t.ticket_type?.name}</td>
                  <td><span className={`pill-st ${t.status}`}>{statusLabel(t.status)}</span></td>
                  <td>{t.checked_in_at ? <small>{fmtDateTime(t.checked_in_at)}</small> : <span style={{ opacity: 0.5 }}>—</span>}</td>
                  <td>
                    <div className="row-actions">
                      {!staff && <Link to={`/orders/${t.order_id}`} className="btn-mini">Order</Link>}
                      <button
                        type="button"
                        onClick={() => openTicketAccess(t.id, "page")}
                        disabled={ticketLinkBusy !== null}
                        title="Open de klantweergave met een tijdelijke admin-link"
                      >
                        {ticketLinkBusy === `page:${t.id}` ? "Openen…" : "Klantpagina"}
                      </button>
                      <button
                        type="button"
                        onClick={() => openTicketAccess(t.id, "pdf")}
                        disabled={ticketLinkBusy !== null}
                        title="Open de PDF van dit ticket met een tijdelijke admin-link"
                      >
                        {ticketLinkBusy === `pdf:${t.id}` ? "Openen…" : "PDF"}
                      </button>
                      {t.status !== "void" && t.status !== "scanned" && (
                        <button onClick={() => voidMut.mutate(t.id)} disabled={voidMut.isPending}>Blokkeer</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && displayed.length === 0 && (
                <tr><td colSpan={7} className="empty">Geen tickets gevonden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
